<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <h3 class="detail-page-title mb-0">{{ supplier.name }}</h3>
          <div class="detail-page-actions">
            <button v-if="helper.checkAccess('edit_suppliers') != -1" @click="editRow" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
              {{$t('edit')}}
            </button>
            <button v-if="helper.checkAccess('delete_suppliers') != -1" @click="deleteRow(supplier)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
              {{$t('delete')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Supplier Info -->
      <div v-else-if="supplier && supplier.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_purchases') != -1">
            <a class="nav-link" :class="{active: step === 'purchases'}" @click="step = 'purchases'">{{$t('purchases')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_receptions') != -1">
            <a class="nav-link" :class="{active: step === 'receptions'}" @click="step = 'receptions'">{{$t('receptions')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_supplier_invoices') != -1">
            <a class="nav-link" :class="{active: step === 'invoices'}" @click="step = 'invoices'">{{$t('invoices')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_payments') != -1">
            <a class="nav-link" :class="{active: step === 'payments'}" @click="step = 'payments'">{{$t('payments')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row g-4 align-items-start">
            <div class="col-lg-8">
              <div class="card supplier-insights-card">
                <div class="card-header bg-white supplier-insights-header">
                  <div>
                    <h6 class="mb-1"><AppIcon name="bi-bar-chart-line" class="bi bi-bar-chart-line me-2" />{{$t('purchase_insights')}}</h6>
                    <small class="text-muted">{{$t('period')}}: {{formattedInsightsPeriod}}</small>
                  </div>
                  <div class="supplier-insights-filters">
                    <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm" :aria-label="$t('filter_by_date')">
                      <option value="all_time">{{$t('all_time')}}</option>
                      <option value="today">{{$t('today')}}</option>
                      <option value="this_month">{{$t('this_month')}}</option>
                      <option value="last_30_days">{{$t('last_30_days')}}</option>
                      <option value="custom">{{$t('custom_date')}}</option>
                    </select>
                    <date-picker
                      v-if="dateRangeOption === 'custom'"
                      v-model="range"
                      type="date"
                      range
                      :clearable="true"
                      :editable="false"
                      :multi-calendars="1"
                      :placeholder="$t('select_date_range')"
                      @change="handleCustomRangeChange"
                      class="supplier-insights-date-picker"
                    ></date-picker>
                    <button type="button" class="btn btn-primary btn-sm text-nowrap" :disabled="insightsLoading" @click="downloadPurchaseReport">
                      <AppIcon name="bi-download" class="bi bi-download me-1" />{{$t('download_report')}}
                    </button>
                  </div>
                </div>

                <div class="card-body">
                  <div v-if="insightsLoading" class="supplier-insights-loading">
                    <div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">Loading...</span></div>
                  </div>

                  <div class="row g-2 mb-3">
                    <div class="col-md-4">
                      <div class="supplier-metric supplier-metric--primary">
                        <span class="supplier-metric-icon"><AppIcon name="bi-cart3" class="bi bi-cart3" /></span>
                        <div><small>{{$t('total_purchases')}}</small><strong>{{formatMoney(purchaseInsights.total_purchases)}}</strong></div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="supplier-metric supplier-metric--success">
                        <span class="supplier-metric-icon"><AppIcon name="bi-check-circle" class="bi bi-check-circle" /></span>
                        <div><small>{{$t('amount_paid')}}</small><strong>{{formatMoney(purchaseInsights.amount_paid)}}</strong></div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="supplier-metric supplier-metric--warning">
                        <span class="supplier-metric-icon"><AppIcon name="bi-hourglass-split" class="bi bi-hourglass-split" /></span>
                        <div><small>{{$t('remaining')}}</small><strong>{{formatMoney(purchaseInsights.remaining)}}</strong></div>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0">{{$t('purchases')}}</h6>
                    <span class="badge bg-light text-dark">{{purchaseInsights.total_purchases_count}} {{$t('purchases')}}</span>
                  </div>
                  <div class="supplier-purchases-list">
                    <table class="table supplier-purchases-table mb-0">
                      <thead>
                        <tr>
                          <th scope="col" class="supplier-purchases-reference-heading"><span>{{$t('reference')}}</span><small>{{$t('date')}}</small></th>
                          <th scope="col">{{$t('products')}}</th>
                          <th scope="col">{{$t('status')}}</th>
                          <th scope="col">{{$t('total')}}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="purchase in purchaseInsights.purchases" :key="`${purchase.source}-${purchase.id}`">
                          <td class="supplier-purchase-reference">
                            <div class="supplier-purchase-reference-value">{{purchase.reference || '-'}}</div>
                            <div class="supplier-purchase-date">{{formatDate(purchase.date)}}</div>
                          </td>
                          <td class="supplier-purchase-products">
                            <template v-if="purchase.products && purchase.products.length">
                              <div v-for="(product, productIndex) in purchase.products" :key="`${purchase.id}-${productIndex}`" class="supplier-purchase-product">
                                <strong>{{product.label}}</strong>
                                <small>{{formatQuantity(product.quantity)}} {{product.unity || $t('units')}} × {{formatMoney(product.unit_price)}}</small>
                              </div>
                            </template>
                            <span v-else class="text-muted">{{$t('no_products')}}</span>
                          </td>
                          <td><span class="badge bg-light text-dark border">{{$t(purchase.status)}}</span></td>
                          <td class="supplier-purchase-money">{{formatMoney(purchase.total)}}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="!purchaseInsights.purchases.length" class="supplier-purchases-empty">
                    <AppIcon name="bi-cart3" class="bi bi-cart3" />
                    <p class="mb-0">{{$t('no_purchases_recorded_yet')}}</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-4">
              <div class="card supplier-side-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-building" class="bi bi-building me-2" />{{$t('supplier')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <dl class="supplier-info-list mb-0">
                    <div><dt>{{$t('supplier')}}</dt><dd class="supplier-info-identity"><span>{{supplier.name || '-'}}</span><small v-if="supplier.reference">{{supplier.reference}}</small></dd></div>
                    <div v-if="supplier.phone"><dt>{{$t('phone')}}</dt><dd>{{supplier.phone}}</dd></div>
                    <div v-if="supplier.email"><dt>{{$t('email')}}</dt><dd>{{supplier.email}}</dd></div>
                    <div v-if="supplierFullAddress"><dt>{{$t('address')}}</dt><dd>{{supplierFullAddress}}</dd></div>
                    <div v-if="supplier.tax_id"><dt>{{$t('tax_id')}}</dt><dd>{{supplier.tax_id}}</dd></div>
                    <div v-if="supplier.vat_number"><dt>{{$t('vat_number')}}</dt><dd>{{supplier.vat_number}}</dd></div>
                  </dl>
                  <div v-if="supplier.notes" class="supplier-notes"><small class="text-muted d-block mb-1">{{$t('notes')}}</small><p class="mb-0">{{supplier.notes}}</p></div>
                </div>
              </div>

              <div class="card mt-4 supplier-side-card">
                <div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-wallet2" class="bi bi-wallet2 me-2" />{{$t('balance')}}</h6></div>
                <div class="card-body">
                  <div class="supplier-balance-chart"><canvas ref="supplierBalanceChart" aria-label="Supplier Balance Chart" role="img"></canvas></div>
                  <div class="supplier-balance-totals">
                    <div><small>{{$t('total')}}</small><strong>{{formatMoney(total)}}</strong></div>
                    <div><small>{{$t('amount_paid')}}</small><strong class="text-success">{{formatMoney(amount_paid)}}</strong></div>
                    <div><small>{{$t('remaining')}}</small><strong :class="total - amount_paid > 0 ? 'text-danger' : 'text-success'">{{formatMoney(total - amount_paid)}}</strong></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'purchases' && helper.checkAccess('view_purchases') != -1">
          <purchase-component v-if="supplier && supplier.id" :supplier_id="this.supplier.id" :supplier="supplier" :isDetailSupplier="true"></purchase-component>
        </div>

        <div v-if="step === 'receptions' && helper.checkAccess('view_receptions') != -1">
          <reception-component v-if="supplier && supplier.id" :supplier_id="this.supplier.id" :supplier="supplier" :isDetailSupplier="true"></reception-component>
        </div>

        <div v-if="step === 'invoices' && helper.checkAccess('view_supplier_invoices') != -1">
          <invoice-component v-if="supplier && supplier.id" :supplier_id="this.supplier.id" :supplier="supplier" :isDetailSupplier="true"></invoice-component>
        </div>

        <div v-if="step === 'payments' && helper.checkAccess('view_payments') != -1">
          <payment-component v-if="supplier && supplier.id" :supplier_id="supplier.id" :isDetailSupplier="true"></payment-component>
        </div>

        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments :entity_id="supplier.id" entity_type="supplier" />
        </div>
      </div>
    </div>



        <!-- Edit Modal -->
        <div class="modal fade" id="supplier-edit-modal" tabindex="-1" aria-labelledby="supplier-edit-modal" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">{{$t('edit_supplier')}}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                </div>
                <div class="modal-body" style="text-align:left">
                    <div class="row">
                        <div class="col-sm-6 mb-3">
                            <label for="labelInput" class="form-label">{{$t('label')}}</label>
                            <input v-model="supplier.name"  type="string" class="form-control" id="labelInput" >
                        </div>
                        <div v-if="isSupplierFieldVisible('email')" class="col-sm-6 mb-3">
                            <label for="emailInput" class="form-label">{{$t('email')}}</label>
                            <input v-model="supplier.email"  type="string" class="form-control" id="emailInput" >
                        </div>
                        <div v-if="isSupplierFieldVisible('phone')" class="col-sm-6 mb-3">
                            <label for="phoneInput" class="form-label">{{$t('phone')}}</label>
                            <input v-model="supplier.phone"  type="string" class="form-control" id="phoneInput" >
                        </div>

                        <div v-if="isSupplierFieldVisible('city')" class="col-sm-6 mb-3">
                            <label for="cityInput" class="form-label">{{$t('city')}}</label>
                            <input v-model="supplier.city" type="string" class="form-control" id="cityInput" >
                        </div>
                        <div v-if="isSupplierFieldVisible('state')" class="col-sm-6 mb-3">
                            <label for="stateInput" class="form-label">{{$t('state')}}</label>
                            <input v-model="supplier.state" type="string" class="form-control" id="stateInput">
                        </div>
                        <div v-if="isSupplierFieldVisible('zip_code')" class="col-sm-6 mb-3">
                            <label for="zipCodeInput" class="form-label">{{$t('zip_code')}}</label>
                            <input v-model="supplier.zip_code" type="string" class="form-control" id="zipCodeInput">
                        </div>
                        <div v-if="isSupplierFieldVisible('country')" class="col-sm-6 mb-3">
                            <label for="countryInput" class="form-label">{{$t('country')}}</label>
                            <input v-model="supplier.country" type="string" class="form-control" id="countryInput">
                        </div>
                        <div v-if="isSupplierFieldVisible('tax_id')" class="col-sm-6 mb-3">
                            <label for="taxIdInput" class="form-label">{{$t('tax_id')}}</label>
                            <input v-model="supplier.tax_id" type="string" class="form-control" id="taxIdInput">
                        </div>
                        <div v-if="isSupplierFieldVisible('vat_number')" class="col-sm-6 mb-3">
                            <label for="vatNumberInput" class="form-label">{{$t('vat_number')}}</label>
                            <input v-model="supplier.vat_number" type="string" class="form-control" id="vatNumberInput" >
                        </div>

                        <div v-if="isSupplierFieldVisible('address')" class="col-sm-12 mb-3">
                            <label for="addressInput" class="form-label">{{$t('address')}}</label>
                            <textarea v-model="supplier.address" class="form-control" id="addressInput" rows="1"></textarea>
                        </div>
                        <div v-if="isSupplierFieldVisible('notes')" class="col-sm-12 mb-3">
                            <collapsible-notes-field v-model="supplier.notes" id="supplier-details-notes" rows="1" />
                        </div>
                        
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}</button>
                    <button type="button" class="btn btn-success" :disabled="loading" @click="update"> <AppIcon name="bi-pencil" class="bi bi-pencil me-2" style="font-size:12px" />{{$t('edit')}}</button>
                </div>
                </div>
            </div>
        </div>
        <!-- Edit modal -->

            
    </div>

</template>

<script>
// @ is an alias to /src
import helper from '../../helper'
import PurchaseComponent from '../../components/PurchaseComponent.vue'
import PaymentComponent from '../../components/PaymentComponent.vue'
import ReceptionComponent from '../../components/ReceptionComponent.vue'
import InvoiceComponent from '../../components/SupplierInvoiceComponent.vue'
import attachments from '../../components/Attachment.vue'
import Chart from 'chart.js'
import DatePicker from 'vue2-datepicker'
import moment from 'moment'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import 'vue2-datepicker/index.css'
import { getCrmSupplierFormFieldDefaults } from '../../constants/crmSupplierFormFields'

export default {
  name: 'SupplierDetails',
  components :{
    PurchaseComponent,
    PaymentComponent,
    ReceptionComponent,
    InvoiceComponent,
    attachments,
    DatePicker
  },
  data(){
    return {
        supplier : {},
        total : 0,
        amount_paid : 0,
        purchaseInsights: {
          total_purchases: 0,
          amount_paid: 0,
          remaining: 0,
          total_purchases_count: 0,
          purchases: []
        },
        dateRangeOption: 'this_month',
        range: [moment().startOf('month').toDate(), new Date()],
        start: moment().startOf('month').format('YYYY-MM-DD'),
        end: moment().format('YYYY-MM-DD'),
        insightsLoading: false,
        insightsRequestId: 0,
        step : 'overview',
        supplierBalanceChart: null,
        loading: false
    }
  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/suppliers')
    this.getSupplier(this.$route.params.id)
    this.fetchPurchaseInsights()
  },
  mounted(){
    window.mixpanel.track("supplier_details");
    //check permissions
    if(helper.checkAccess('view_suppliers') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_suppliers') == -2) return this.$router.push({path : '/subscribe'})

        
    // eslint-disable-next-line no-undef
    this.editModal = new bootstrap.Modal(document.getElementById('supplier-edit-modal'),{backdrop: 'static', keyboard: false})
  },
  beforeDestroy(){
    if(this.supplierBalanceChart){
      this.supplierBalanceChart.destroy()
      this.supplierBalanceChart = null
    }
  },
  watch: {
    step(newVal) {
      if (newVal === 'overview') {
        this.$nextTick(() => this.buildSupplierBalanceChart())
      }
    }
  },
    methods : {
        isSupplierFieldVisible(field){
            return this.supplierFormFields[field] !== false
        },
        async getSupplier(id){
        try{
            this.loading = true
            const response = await helper.customGet(`${process.env.VUE_APP_API}/suppliers/${id}`)
            this.supplier = response.data.supplier
            this.total = response.data.total
            this.amount_paid = response.data.amount_paid
            this.loading = false
            this.$nextTick(() => this.buildSupplierBalanceChart())
        }catch(error){
            this.$router.push({path : '/suppliers'})
            this.loading = false
        }
        },
        async fetchPurchaseInsights(){
            const requestId = ++this.insightsRequestId
            this.insightsLoading = true
            try{
                const params = new URLSearchParams()
                if(this.start && this.end){
                    params.append('start', this.start)
                    params.append('end', this.end)
                }
                const query = params.toString() ? `?${params.toString()}` : ''
                const response = await helper.customGet(`${process.env.VUE_APP_API}/suppliers/${this.$route.params.id}/purchase-insights${query}`)
                if(requestId !== this.insightsRequestId) return

                this.purchaseInsights = {
                    total_purchases: Number(response.data.total_purchases) || 0,
                    amount_paid: Number(response.data.amount_paid) || 0,
                    remaining: Number(response.data.remaining) || 0,
                    total_purchases_count: Number(response.data.total_purchases_count) || 0,
                    purchases: Array.isArray(response.data.purchases) ? response.data.purchases : []
                }
            }catch(error){
                if(requestId === this.insightsRequestId) this.$toast.error(this.$t('failed_to_fetch_data'))
            }finally{
                if(requestId === this.insightsRequestId) this.insightsLoading = false
            }
        },
        handleDateRangeChange(){
            const today = moment()
            if(this.dateRangeOption === 'all_time'){
                this.range = []
                this.start = null
                this.end = null
            }else if(this.dateRangeOption === 'today'){
                this.range = [today.toDate(), today.toDate()]
                this.start = today.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else if(this.dateRangeOption === 'this_month'){
                const monthStart = moment().startOf('month')
                this.range = [monthStart.toDate(), today.toDate()]
                this.start = monthStart.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else if(this.dateRangeOption === 'last_30_days'){
                const periodStart = moment().subtract(29, 'days')
                this.range = [periodStart.toDate(), today.toDate()]
                this.start = periodStart.format('YYYY-MM-DD')
                this.end = today.format('YYYY-MM-DD')
            }else{
                this.range = []
                return
            }
            this.fetchPurchaseInsights()
        },
        handleCustomRangeChange(range){
            if(!range || !range[0] || !range[1]) return
            this.start = moment(range[0]).format('YYYY-MM-DD')
            this.end = moment(range[1]).format('YYYY-MM-DD')
            this.fetchPurchaseInsights()
        },
        formatQuantity(value){
            const number = Number(value) || 0
            return number.toLocaleString(this.$i18n.locale, {maximumFractionDigits: 3})
        },
        downloadPurchaseReport(){
            const doc = new jsPDF({orientation: 'landscape'})
            const pageWidth = doc.internal.pageSize.getWidth()
            const pageHeight = doc.internal.pageSize.getHeight()
            const margin = 14
            const contentWidth = pageWidth - (margin * 2)
            const reportTitle = String(this.$t('purchase_insights'))
            const supplierName = this.supplier.name || '-'

            doc.setTextColor(35, 35, 35)
            doc.setFont('helvetica', 'bold')
            doc.setFontSize(16)
            doc.text(reportTitle, margin, 16)
            doc.setDrawColor(210, 210, 210)
            doc.setLineWidth(0.4)
            doc.line(margin, 22, pageWidth - margin, 22)

            doc.setTextColor(70, 70, 70)
            doc.setFont('helvetica', 'normal')
            doc.setFontSize(10)
            doc.text(`${String(this.$t('supplier'))}: ${supplierName}${this.supplier.reference ? `  ·  ${this.supplier.reference}` : ''}`, margin, 31)
            doc.text(`${String(this.$t('period'))}: ${this.formattedInsightsPeriod}`, margin, 38)

            const summaryGap = 4
            const summaryWidth = (contentWidth - (summaryGap * 2)) / 3
            const summaryItems = [
                {label: this.$t('total_purchases'), value: this.formatMoney(this.purchaseInsights.total_purchases)},
                {label: this.$t('amount_paid'), value: this.formatMoney(this.purchaseInsights.amount_paid)},
                {label: this.$t('remaining'), value: this.formatMoney(this.purchaseInsights.remaining)}
            ]
            summaryItems.forEach((item, index) => {
                const x = margin + (index * (summaryWidth + summaryGap))
                if(index > 0){
                    doc.setDrawColor(220, 220, 220)
                    doc.line(x - (summaryGap / 2), 48, x - (summaryGap / 2), 68)
                }
                doc.setTextColor(105, 105, 105)
                doc.setFont('helvetica', 'normal')
                doc.setFontSize(9)
                doc.text(String(item.label), x + 5, 56)
                doc.setTextColor(35, 35, 35)
                doc.setFont('helvetica', 'bold')
                doc.setFontSize(12)
                doc.text(String(item.value), x + 5, 65)
            })

            doc.setDrawColor(210, 210, 210)
            doc.line(margin, 73, pageWidth - margin, 73)

            doc.setTextColor(35, 35, 35)
            doc.setFont('helvetica', 'bold')
            doc.setFontSize(12)
            doc.text(String(this.$t('purchases')), margin, 82)
            doc.setFont('helvetica', 'normal')
            doc.setFontSize(9)
            doc.setTextColor(105, 105, 105)
            doc.text(`${this.purchaseInsights.total_purchases_count} ${String(this.$t('purchases'))}`, pageWidth - margin, 82, {align: 'right'})

            const tableY = 87
            if(!this.purchaseInsights.purchases.length){
                doc.setDrawColor(220, 220, 220)
                doc.roundedRect(margin, tableY, contentWidth, 28, 2, 2)
                doc.setTextColor(125, 125, 125)
                doc.setFontSize(10)
                doc.text(String(this.$t('no_purchases_recorded_yet')), pageWidth / 2, tableY + 16, {align: 'center'})
            }else{
                autoTable(doc, {
                    startY: tableY,
                    head: [[
                        `${String(this.$t('reference'))}\n${String(this.$t('date'))}`,
                        String(this.$t('products')),
                        String(this.$t('status')),
                        String(this.$t('total'))
                    ]],
                    body: this.purchaseInsights.purchases.map(purchase => {
                        const products = purchase.products && purchase.products.length
                            ? purchase.products.map(product => {
                                const quantity = `${this.formatQuantity(product.quantity)} ${product.unity || String(this.$t('units'))}`
                                return `${product.label || '-'}\n${quantity} × ${this.formatMoney(product.unit_price)}`
                            }).join('\n\n')
                            : String(this.$t('no_products'))
                        return [
                            `${purchase.reference || '-'}\n${this.formatDate(purchase.date)}`,
                            products,
                            String(this.$t(purchase.status)),
                            this.formatMoney(purchase.total)
                        ]
                    }),
                    theme: 'grid',
                    margin: {left: margin, right: margin, bottom: 14},
                    styles: {fontSize: 9, cellPadding: 4, halign: 'center', valign: 'middle', lineColor: [220, 220, 220], lineWidth: 0.2, textColor: [45, 45, 45]},
                    headStyles: {fillColor: [245, 245, 245], textColor: [55, 55, 55], fontStyle: 'bold', halign: 'center'},
                    columnStyles: {
                        0: {cellWidth: 52, fontSize: 8},
                        1: {cellWidth: 125},
                        2: {cellWidth: 35},
                        3: {cellWidth: 45, fontStyle: 'bold'}
                    },
                    didParseCell(data){
                        if(data.section === 'body' && data.column.index === 0) data.cell.styles.fontStyle = 'bold'
                    }
                })
            }

            const pageCount = doc.getNumberOfPages()
            for(let page = 1; page <= pageCount; page++){
                doc.setPage(page)
                doc.setFont('helvetica', 'normal')
                doc.setFontSize(7)
                doc.setTextColor(125, 125, 125)
                doc.text(`${page} / ${pageCount}`, pageWidth - margin, pageHeight - 6, {align: 'right'})
            }

            const safeSupplierName = supplierName.replace(/[^a-z0-9-_]/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'supplier'
            const periodName = this.start && this.end ? `${this.start}_${this.end}` : 'all-time'
            doc.save(`${safeSupplierName}-purchases-${periodName}.pdf`)
        },
        buildSupplierBalanceChart(){
            const ctx = this.$refs.supplierBalanceChart
            if(!ctx){ return }
            if(this.supplierBalanceChart){
              this.supplierBalanceChart.destroy()
              this.supplierBalanceChart = null
            }
            const total = Number(this.total) || 0
            const paid = Number(this.amount_paid) || 0
            const remaining = Math.max(total - paid, 0)
            const hasData = (paid + remaining) > 0
            const data = hasData ? [paid, remaining] : [1, 0]
            const labels = [this.$t('amount_paid'), this.$t('remaining')]
            this.supplierBalanceChart = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels,
                    datasets: [{
                        data,
                        backgroundColor: ['#34c38f', '#ff6b6b'],
                        borderColor: ['#ffffff', '#ffffff'],
                        borderWidth: 2
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: { position: 'bottom' },
                    tooltips: {
                        callbacks: {
                            label: (tooltipItem, chartData) => {
                                const idx = tooltipItem.index
                                const label = chartData.labels[idx]
                                const val = chartData.datasets[0].data[idx]
                                return `${label}: ${this.formatMoney ? this.formatMoney(val) : val}`
                            }
                        }
                    }
                }
            })
        },
        editRow(){
            this.editModal.show()
        },
        async update(){
            try{
                this.loading = true
                await helper.customPatch(`${process.env.VUE_APP_API}/suppliers/${this.supplier.id}`,this.supplier)
                this.loading = false
                this.editModal.hide()
                this.getSupplier(this.$route.params.id)
            }catch(error){
                this.loading = false
            }

        },
        async deleteRow(row){
                this.$swal.fire({
                title: `${this.$t('are_you_sure')}?`,
                text : this.$t('you_cant_revert'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('yes_delete_it')
                }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/suppliers/${row.id}`)
                    this.$router.push({path : '/suppliers'})
                }
                })
        },
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);}
    },
    computed : {
        business(){ return this.$store.getters.BUSINESS},
        supplierFormFields(){
            return {
                ...getCrmSupplierFormFieldDefaults(),
                ...(this.business?.metadata?.crm_supplier_form_fields || {}),
                name: true
            }
        },
        formattedInsightsPeriod(){
            if(!this.start || !this.end) return this.$t('all_time')
            return `${moment(this.start).format('DD/MM/YYYY')} - ${moment(this.end).format('DD/MM/YYYY')}`
        },
        supplierFullAddress(){
            return [this.supplier.address, this.supplier.city, this.supplier.state, this.supplier.zip_code, this.supplier.country]
                .filter(Boolean)
                .join(', ')
        },
        helper(){ return helper},
    }
}
</script>

<style scoped>
.supplier-insights-card,
.supplier-side-card {
  border: 1px solid #e7eaf0;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(20, 35, 55, 0.04);
  overflow: hidden;
}

.supplier-insights-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
}

.supplier-insights-filters {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 8px;
  max-width: 100%;
  overflow-x: auto;
}

.supplier-insights-filters .form-select {
  width: 145px;
  min-width: 145px;
  min-height: 36px;
  font-size: 0.9rem;
}

.supplier-insights-date-picker {
  width: 205px;
  min-width: 205px;
}

.supplier-insights-filters .btn {
  flex: 0 0 auto;
  min-height: 36px;
  font-size: 0.9rem;
}

::v-deep .supplier-insights-date-picker .mx-input {
  height: 36px;
  font-size: 0.9rem;
}

.supplier-insights-card .card-body {
  position: relative;
}

.supplier-insights-loading {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.7);
}

.supplier-metric {
  min-height: 70px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e9edf3;
  border-radius: 8px;
  background: #fbfcfe;
}

.supplier-metric-icon {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 1rem;
}

.supplier-metric small,
.supplier-metric strong {
  display: block;
}

.supplier-metric small {
  margin-bottom: 1px;
  color: #6c757d;
  font-size: 0.78rem;
}

.supplier-metric strong {
  color: #25364b;
  font-size: 1rem;
  line-height: 1.25;
}

.supplier-metric--primary .supplier-metric-icon { color: #3568d4; background: #eaf0ff; }
.supplier-metric--success .supplier-metric-icon { color: #1f9d67; background: #e8f7f0; }
.supplier-metric--warning .supplier-metric-icon { color: #d3831f; background: #fff3df; }

.supplier-purchases-list {
  max-height: 560px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow-y: auto;
}

.supplier-purchases-table {
  min-width: 780px;
  border-collapse: separate;
  border-spacing: 0;
  color: #2d3b4f;
  font-size: 0.84rem;
}

.supplier-purchases-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 11px 14px;
  border-bottom: 1px solid #ced4da;
  background: #f8f9fa;
  color: #5d6878;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}

.supplier-purchases-reference-heading span,
.supplier-purchases-reference-heading small {
  display: block;
}

.supplier-purchases-reference-heading small {
  margin-top: 1px;
  color: #7b8797;
  font-size: 0.68rem;
  font-weight: 500;
}

.supplier-purchases-table tbody td {
  padding: 14px 16px;
  border-color: #e9ecef;
  text-align: center;
  vertical-align: middle;
}

.supplier-purchase-reference {
  width: 20%;
  white-space: nowrap;
}

.supplier-purchase-reference-value {
  color: #26374d;
  font-size: 0.75rem;
  font-weight: 600;
}

.supplier-purchase-date {
  margin-top: 3px;
  color: #7b8797;
  font-size: 0.68rem;
}

.supplier-purchase-products {
  width: 52%;
}

.supplier-purchase-product + .supplier-purchase-product {
  padding-top: 7px;
  margin-top: 7px;
  border-top: 1px solid #edf0f3;
}

.supplier-purchase-product strong,
.supplier-purchase-product small {
  display: block;
}

.supplier-purchase-product strong {
  font-size: 0.84rem;
}

.supplier-purchase-product small {
  margin-top: 2px;
  color: #6f7d90;
  font-size: 0.75rem;
}

.supplier-purchase-money {
  font-weight: 600;
  white-space: nowrap;
}

.supplier-purchases-empty {
  min-height: 190px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border: 1px dashed #dce2ea;
  border-radius: 9px;
  color: #8a96a6;
}

.supplier-purchases-empty i {
  font-size: 2rem;
}

.supplier-side-card .card-header {
  padding: 11px 14px;
  border-bottom: 1px solid #edf0f4;
}

.supplier-side-card .card-body {
  padding: 10px 14px 14px;
}

.supplier-info-list > div {
  display: grid;
  grid-template-columns: minmax(80px, 0.7fr) minmax(0, 1.3fr);
  align-items: baseline;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f2f5;
}

.supplier-info-list dt,
.supplier-info-list dd {
  margin: 0;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
}

.supplier-info-list dt {
  color: #7b8797;
  font-weight: 500;
}

.supplier-info-list dd {
  color: #2d3b4f;
  font-weight: 600;
  text-align: end;
}

.supplier-info-identity {
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.supplier-info-identity span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supplier-info-identity small {
  flex: 0 0 auto;
  color: #8a96a6;
  font-size: 0.72rem;
  font-weight: 500;
}

.supplier-notes {
  padding: 9px 10px;
  margin-top: 10px;
  border-radius: 8px;
  background: #f7f9fc;
  font-size: 0.82rem;
}

.supplier-balance-chart {
  position: relative;
  height: 160px;
  margin-bottom: 14px;
}

.supplier-balance-totals {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding-top: 14px;
  border-top: 1px solid #edf0f4;
  text-align: center;
}

.supplier-balance-totals > div + div {
  border-inline-start: 1px solid #edf0f4;
}

.supplier-balance-totals small,
.supplier-balance-totals strong {
  display: block;
}

.supplier-balance-totals small {
  margin-bottom: 4px;
  color: #6f7d90;
  font-size: 0.78rem;
}

.supplier-balance-totals strong {
  padding: 0 4px;
  font-size: 0.88rem;
  overflow-wrap: anywhere;
}

@media (max-width: 1399.98px) {
  .supplier-insights-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .supplier-insights-filters {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 575.98px) {
  .supplier-insights-filters {
    flex-wrap: wrap;
    overflow: visible;
  }

  .supplier-insights-date-picker,
  .supplier-insights-filters .form-select,
  .supplier-insights-filters .btn {
    width: 100%;
  }
}
</style>
